/**
 * Was this uploaded document actually read, and how much came back — answered in
 * one place for the partner portal and the reviewer screen.
 *
 * Both screens used to answer it by counting how many form fields the AI filled
 * from a file, which makes a document HMS never managed to read look identical to
 * one that simply held nothing the form asks for. A run now records what happened
 * to each file, so the screens can stop guessing — and so a run that read nothing
 * can never draw a green tick again.
 *
 * The read records arrive on the case, not on the document: ai_extraction_data
 * .documents for a reviewer (document_id, filename, doc_type, status, characters,
 * characters_used, pages_attempted, pages_with_text, message) and the narrower
 * ai_info.documents for a partner (document_id, filename, read, reason). Both
 * spellings of the same two facts are read here, and a record carrying neither is
 * 'unknown' rather than a guess — a case analysed before the records existed has
 * none at all, and every screen has to be able to say so without inventing a
 * cause. A reading result found on the document itself is honoured too, so moving
 * these facts onto the document serializer later breaks neither screen.
 */

/** The case says a finished run got no words out of a single uploaded file. */
export const EXTRACTION_STATUS_UNREADABLE = 'unreadable';

/** extraction_status values that describe a run that is over. 'idle' is here
 *  because both screens only draw this panel once a run has happened at all, and
 *  queueing the next one can drop the case back to idle — the last finished
 *  result is still the honest thing to show, and files uploaded since then report
 *  themselves as not read yet. Anything NOT in this list — including a state
 *  added after this file was written — must never reach the success panel: an
 *  unearned tick is worse than no tick. */
const FINISHED_STATUSES = ['done', 'idle'];

/** Colour role each reading state carries, mapped to each screen's own classes. */
export const READING_TONES = {
    read: 'ok',
    blank: 'warn',
    unreadable: 'bad',
    not_analysed: 'muted',
    unknown: 'muted',
};

const EMPTY_READING = {
    state: 'unknown',
    characters: null,
    charactersUsed: null,
    pagesAttempted: null,
    pagesWithText: null,
    message: '',
};

const toCount = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const count = Number(value);
    return Number.isFinite(count) ? count : null;
};

const messageOf = (record) => String(record.message || record.reason || '').trim();

/** Which of the five things we can honestly say about one file, from whichever
 *  of the two record shapes arrived. */
const readingState = (record) => {
    const status = String(record.status || '').trim();
    if (status === 'read' || record.read === true) {
        // Nought characters from a reader that ran cleanly is a blank page, not a
        // refusal. A record that never carried a character count says 'read'.
        return toCount(record.characters) === 0 ? 'blank' : 'read';
    }
    if (status === 'unreadable') return 'unreadable';
    if (status === 'not_analysed') return 'not_analysed';
    // read === false on its own is not evidence of a refusal: it is also what an
    // absent reading result looks like once it has been through a boolean.
    if (record.read === false && messageOf(record)) return 'unreadable';
    return 'unknown';
};

const readingFrom = (record) => ({
    state: readingState(record),
    characters: toCount(record.characters),
    charactersUsed: toCount(record.characters_used),
    pagesAttempted: toCount(record.pages_attempted),
    pagesWithText: toCount(record.pages_with_text),
    message: messageOf(record),
});

/** A reading result kept on the document rather than on the case, nested under
 *  `reading` or flattened onto `reading_*`. */
const documentOwnRecord = (document) => {
    if (document.reading && typeof document.reading === 'object') return document.reading;
    if (document.reading_status !== undefined) {
        return {
            status: document.reading_status,
            characters: document.reading_characters,
            characters_used: document.reading_characters_used,
            pages_attempted: document.reading_pages_attempted,
            pages_with_text: document.reading_pages_with_text,
            message: document.reading_message,
        };
    }
    return null;
};

/**
 * The last run's read records, keyed both ways: either the document id or the
 * filename identifies a file, and only one of them survives a re-upload.
 */
export const readRecordIndex = (aiPayload) => {
    const records = (((aiPayload || {}).documents) || []).filter(
        (record) => record && typeof record === 'object',
    );
    const byId = {};
    const byFilename = {};
    records.forEach((record) => {
        if (record.document_id !== null && record.document_id !== undefined) {
            byId[record.document_id] = record;
        }
        if (record.filename) byFilename[record.filename] = record;
    });
    return { records, byId, byFilename };
};

/** What happened to one uploaded file on the last run. `index` is optional so a
 *  caller holding only the document can still ask. */
export const readingSummary = (doc, index) => {
    const document = doc || {};
    const found = index
        ? index.byId[document.id] ?? index.byFilename[document.original_filename]
        : null;
    const record = found || documentOwnRecord(document);
    if (!record) {
        // No record for this file. It was uploaded since the last run — unless the
        // run recorded nothing at all, and then we simply do not know.
        const analysed = index && index.records.length > 0;
        return { ...EMPTY_READING, state: analysed ? 'not_analysed' : 'unknown' };
    }
    return readingFrom(record);
};

/** How many of these documents landed in each reading state. */
export const readingStateCounts = (documents, index) => {
    const counts = { read: 0, blank: 0, unreadable: 0, not_analysed: 0, unknown: 0 };
    (documents || []).forEach((doc) => {
        const { state } = readingSummary(doc, index);
        counts[state] += 1;
    });
    return counts;
};

/**
 * Which of the six things we can honestly say about the last analysis:
 *   'checking'        — still running, or ended in a state this screen does not
 *                       know, so nothing may be claimed about it.
 *   'unread'          — documents were sent and not one word came back.
 *   'error'           — the run itself broke, or was interrupted part-way.
 *   'nothing_matched' — files were read, none of the form's details were in them.
 *   'ok'              — at least one field was pre-filled.
 * `aiPayload` carries the read records (ai_info on the portal,
 * ai_extraction_data on the review screen); pass `documents` from the case.
 */
export const analysisOutcome = ({
    documents = [],
    aiPayload = null,
    extractionStatus = '',
    filledFieldCount = 0,
} = {}) => {
    const index = readRecordIndex(aiPayload);
    const counts = readingStateCounts(documents, index);
    const attempted = counts.read + counts.blank + counts.unreadable;

    // The case says so, or every file we can account for came back unreadable.
    // Either way this is not a finished analysis, whatever the status column says.
    const nothingRead = extractionStatus === EXTRACTION_STATUS_UNREADABLE
        || (counts.unreadable > 0 && counts.read === 0 && counts.blank === 0);

    let kind;
    if (extractionStatus === 'queued' || extractionStatus === 'running') {
        kind = 'checking';
    } else if (nothingRead) {
        kind = 'unread';
    } else if (extractionStatus === 'failed') {
        kind = 'error';
    } else if (!FINISHED_STATUSES.includes(extractionStatus)) {
        kind = 'checking';
    } else if (filledFieldCount === 0) {
        kind = 'nothing_matched';
    } else {
        kind = 'ok';
    }

    return {
        kind,
        index,
        counts,
        attempted,
        readCount: counts.read + counts.blank,
        unreadableCount: counts.unreadable,
        // The sentence the run stored about why it produced nothing. Written for a
        // partner, so it is safe on either screen — but the portal deliberately
        // does not use it for an unreadable document (see KYCPortalOnboarding).
        failureReason: String((aiPayload || {}).failure_reason || '').trim(),
    };
};
